import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Card, DatePicker, Empty, Skeleton, Statistic, Table, Tag, Typography,
} from "antd";
import {
  ArrowDownOutlined, ArrowUpOutlined, BankOutlined, WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getDashboardSummary } from "../services/dashboardApi";

const MONEY = new Intl.NumberFormat("vi-VN", {
  style: "currency", currency: "VND", maximumFractionDigits: 0,
});
const SHORT_DATE = new Intl.DateTimeFormat("vi-VN", { day: "2-digit", month: "2-digit" });
const DATE_TIME = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "short", timeStyle: "short",
});

export default function Dashboard() {
  const [range, setRange] = useState([
    moment().startOf("month"),
    moment(),
  ]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const sequence = useRef(0);

  async function load(nextRange = range) {
    const requestSequence = ++sequence.current;
    setLoading(true);
    setError("");
    try {
      const result = await getDashboardSummary(
        nextRange[0].format("YYYY-MM-DD"),
        nextRange[1].format("YYYY-MM-DD")
      );
      if (requestSequence === sequence.current) setSummary(result);
    } catch (requestError) {
      if (requestSequence === sequence.current) {
        setError(requestError.message || "Không thể tải dữ liệu tổng quan");
      }
    } finally {
      if (requestSequence === sequence.current) setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountNames = new Map(
    (summary?.accounts || []).map((account) => [account.accountId, account.internalCode])
  );
  const maxDaily = Math.max(
    1,
    ...(summary?.dailyCashFlow || []).flatMap((point) => [point.incoming, point.outgoing])
  );

  const accountColumns = [
    { title: "Tài khoản", dataIndex: "internalCode" },
    { title: "Ngân hàng", dataIndex: "bankName" },
    {
      title: "Số tài khoản",
      render: (_, row) => row.accountNumber || row.accountNumberMasked || "—",
    },
    {
      title: "Số dư đầu", dataIndex: "openingBalance", align: "right",
      render: (value) => MONEY.format(value),
    },
    {
      title: "Số dư hiện tại", dataIndex: "balance", align: "right",
      render: (value) => <strong className={value < 0 ? "money-out" : ""}>{MONEY.format(value)}</strong>,
    },
  ];
  const transactionColumns = [
    {
      title: "Thời gian", dataIndex: "transactionAt", width: 145,
      render: (value) => DATE_TIME.format(new Date(value)),
    },
    {
      title: "Tài khoản", dataIndex: "bankAccountId",
      render: (value) => accountNames.get(value) || "—",
    },
    {
      title: "Loại", dataIndex: "type", width: 90,
      render: (value) => <Tag color={value === "IN" ? "green" : "volcano"}>{value === "IN" ? "Tiền vào" : "Tiền ra"}</Tag>,
    },
    {
      title: "Số tiền", dataIndex: "amount", align: "right",
      render: (value, row) => `${row.type === "IN" ? "+" : "-"}${MONEY.format(value)}`,
    },
    { title: "Nội dung", dataIndex: "content", render: (value) => value || "—" },
  ];

  return (
    <div className="management-page">
      <div className="page-heading">
        <div>
          <Typography.Title level={2}>Tổng quan dòng tiền</Typography.Title>
          <Typography.Text type="secondary">
            Số dư hiện tại và biến động trong kỳ trên các tài khoản bạn được phép xem.
          </Typography.Text>
        </div>
        <DatePicker.RangePicker
          value={range}
          format="DD/MM/YYYY"
          allowClear={false}
          onChange={(value) => {
            setRange(value);
            load(value);
          }}
        />
      </div>
      {error && <Alert className="page-alert" type="error" showIcon message={error} />}
      {loading && !summary ? <Skeleton active /> : (
        <>
          <div className="dashboard-kpis">
            <Card><Statistic title="Tổng số dư hiện tại" value={summary?.totalBalance || 0} formatter={(value) => MONEY.format(Number(value))} prefix={<BankOutlined />} /></Card>
            <Card><Statistic title="Tiền vào trong kỳ" value={summary?.periodIncoming || 0} formatter={(value) => MONEY.format(Number(value))} prefix={<ArrowDownOutlined />} valueStyle={{ color: "#237804" }} /></Card>
            <Card><Statistic title="Tiền ra trong kỳ" value={summary?.periodOutgoing || 0} formatter={(value) => MONEY.format(Number(value))} prefix={<ArrowUpOutlined />} valueStyle={{ color: "#cf1322" }} /></Card>
            <Card><Statistic title="Chênh lệch đối soát" value={summary?.discrepancy || 0} formatter={(value) => MONEY.format(Number(value))} prefix={<WarningOutlined />} /></Card>
          </div>

          <div className="dashboard-grid">
            <Card title="Dòng tiền theo ngày" loading={loading}>
              {(summary?.dailyCashFlow || []).length ? (
                <div className="cash-flow-chart">
                  {summary.dailyCashFlow.map((point) => (
                    <div className="cash-flow-day" key={point.date}>
                      <div className="cash-flow-bars">
                        <span className="cash-flow-in" style={{ height: `${Math.max(3, point.incoming / maxDaily * 120)}px` }} title={`Vào: ${MONEY.format(point.incoming)}`} />
                        <span className="cash-flow-out" style={{ height: `${Math.max(3, point.outgoing / maxDaily * 120)}px` }} title={`Ra: ${MONEY.format(point.outgoing)}`} />
                      </div>
                      <small>{SHORT_DATE.format(new Date(`${point.date}T00:00:00`))}</small>
                    </div>
                  ))}
                </div>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có dòng tiền trong kỳ" />}
              <div className="chart-legend"><span className="legend-in" /> Tiền vào <span className="legend-out" /> Tiền ra</div>
            </Card>
            <Card title="Số dư theo tài khoản" loading={loading}>
              <Table rowKey="accountId" columns={accountColumns} dataSource={summary?.accounts || []} pagination={false} size="small" scroll={{ x: 650 }} />
            </Card>
          </div>

          <Card title="Giao dịch mới nhất" loading={loading}>
            <Table rowKey="id" columns={transactionColumns} dataSource={summary?.latestTransactions || []} pagination={false} size="small" scroll={{ x: 750 }} />
          </Card>
        </>
      )}
    </div>
  );
}
